// The products a person confirmed for the PLL clip (pll-s02e07-1110-1210 and its 30-second cut
// pll-s02e07-1110-1140), garment by garment.
//
// The pipeline's catalog matches for this clip were wrong for the two girls in the first scene
// (a swimsuit for the dress, a bag and an anklet for the green tee and the cardigan...). Jason
// identified the real garments (2026-10-22) and where they are on sale; this file is what the
// client sees for those garments, applied on top of the published clip (`Show.curate`).
//
// Frame-by-frame, from the clip itself (1439 frames at 23.976 fps):
//   frames    0-22   wide shot, both girls: track 0 = Faran's green tee, track 1 = her shorts
//   frames   23-140  Kelly: two garments now, the cardigan (23-131) and the dress under it (23-140)
//   frames  141-338  close-ups: track 4 = Faran's tee (two segments); track 5 = Kelly (179-237)
//   frames  339-409  Kelly: track 6
//   frames  410-697  Faran: track 7 = tee; Kelly in the foreground / profile: track 8 (580-697)
//   frames  698+     the Orpheum (uniforms, the guy's tee): not in this list, the catalog match stays
//
// Girl 1 = Faran (Zaria): Simon Miller Cyclone Baby Tee, Alexis Costa Belted Short.
// Girl 2 = Kelly (Mallory Bechtel): Reformation Noha Dress in Hillside, plus a grey-blue ribbed
// 3/4-sleeve open cardigan that is NOT identified anywhere (WornOnTV, ShopYourTV, our models, the dev
// orchestrator, Google Lens; it is not the Majorelle Mia); a similar style rides along with the dress.
import type { PublishedClip, PublishedProduct } from '../../utils/clipApi';
import { curateWith, type ConfirmedProduct } from '../confirmed';

type Confirmed = Omit<PublishedProduct, 'gallery_row' | 'image' | 'score' | 'listings' | 'match_type'> &
  Partial<Pick<PublishedProduct, 'gallery_row' | 'image' | 'score' | 'listings' | 'match_type'>>;

const confirmed = (product: Confirmed): PublishedProduct => ({
  gallery_row: -1,
  image: null,
  score: null,
  listings: null,
  match_type: 'Confirmed match',
  ...product,
});

const SIMON_MILLER_TEE = confirmed({
  brand: 'Simon Miller',
  name: 'Cyclone Baby Tee',
  color: 'Grass Green',
  category: 'Apparel & Accessories > Clothing > Shirts & Tops',
  merchant: 'Poshmark',
  image_url: '/shows/pll/simon-miller-cyclone-baby-tee.jpg',
  price: '$65–$85',
  links: [
    {
      marketplace: 'Poshmark',
      url: 'https://poshmark.com/listing/Simon-Miller-Green-Cyclone-Baby-TShirt-650f40b6dff1f14abd00d4da',
      price: '$85',
      size: 'S',
    },
    {
      marketplace: 'Poshmark',
      url: 'https://poshmark.com/listing/Simon-Miller-green-knit-baby-Tee-64e368953b982afa3286d181',
      price: '$65',
      size: 'XS',
    },
  ],
});

const ALEXIS_COSTA_SHORT = confirmed({
  brand: 'Alexis',
  name: 'Costa Belted Short',
  color: 'Multicolor Print',
  category: 'Apparel & Accessories > Clothing > Shorts',
  merchant: 'eBay',
  image_url: '/shows/pll/alexis-costa-belted-short.jpg',
  price: '$120–$180',
  links: [
    { marketplace: 'eBay', url: 'https://www.ebay.com/itm/377155719784', price: '$180', size: 'M', note: 'New without tags · or best offer' },
    { marketplace: 'eBay', url: 'https://www.ebay.com/itm/177035217042', price: '$120', size: 'L', note: 'Deadstock · or best offer' },
  ],
});

const REFORMATION_NOHA_DRESS = confirmed({
  brand: 'Reformation',
  name: 'Noha Dress',
  color: 'Hillside',
  category: 'Apparel & Accessories > Clothing > Dresses',
  merchant: 'Poshmark',
  image_url: '/shows/pll/reformation-noha-dress-hillside.jpg',
  price: '$78–$111',
  links: [
    {
      marketplace: 'Poshmark',
      url: 'https://poshmark.com/listing/Reformation-Noha-Dress-in-Hillside-6977d7bda0dc2bb4c93a7065',
      price: '$100',
      size: 'Various',
    },
    {
      marketplace: 'Poshmark',
      url: 'https://poshmark.com/listing/Reformation-Noha-Mini-Dress-in-Hillside-Floral-Blue-and-Green-Size-0-68ab3eb1d8024cd51ce14ac3',
      price: '$78',
      size: '0',
    },
    {
      marketplace: 'Poshmark',
      url: 'https://poshmark.com/listing/reformation-noha-mini-dress-hillside-great-condition-67c1e3019d05c4920d594a21',
      price: '$111',
      size: '6',
      note: 'Great condition',
    },
  ],
});

// Kelly's grey-blue ribbed cardigan is not catalogued anywhere (WornOnTV, ShopYourTV, a reverse image
// search), so this is a SIMILAR STYLE, not the garment (Felipe, 2026-10-22: "can we not search for a
// similar cardigan?"): a light blue open-front ribbed-trim 3/4-sleeve cardigan at Target. The drawer
// says "Similar style" where the others say "Confirmed match".
const SIMILAR_CARDIGAN = confirmed({
  match_type: 'Similar style',
  brand: 'EadianDree',
  name: 'Cropped Open Front Cardigan, 3/4 Sleeve Ribbed Knit',
  color: 'Light Blue',
  category: 'Apparel & Accessories > Clothing > Outerwear > Cardigans',
  merchant: 'Target',
  image_url: '/shows/pll/similar-cardigan-light-blue.jpg',
  price: '$32.99',
  links: [
    {
      marketplace: 'Target',
      url: 'https://www.target.com/p/women-s-cropped-open-front-cardigan-3-4-sleeve-ribbed-knit-shrug-light-blue-large/-/A-1012406688',
      price: '$32.99',
      size: 'S–XL',
    },
  ],
});

// Where each garment is: frame ranges and the hotspot's mean position (from the 2026-10-22 00:16Z run,
// measured on published.json). A rerun that renumbers tracks still finds them by these.
export const PLL_CONFIRMED: ConfirmedProduct[] = [
  {
    product: SIMON_MILLER_TEE,
    garments: [
      { frames: [[0, 22]], at: [0.73, 0.62] },
      { frames: [[141, 178], [238, 338]], at: [0.6, 0.82] },
      { frames: [[410, 592]], at: [0.66, 0.82] },
    ],
  },
  {
    product: ALEXIS_COSTA_SHORT,
    garments: [{ frames: [[0, 22]], at: [0.73, 0.87] }],
  },
  {
    // Kelly's cardigan. In the first Kelly shot it is now its own garment: --merge_contain 0.3
    // (silvr-ml-demo #5) stops the track merge from swallowing a garment worn under another, and the
    // 2026-10-23 run splits that shot into the cardigan here and the dress below, each with a real
    // track of its own. In the close-ups the detector still returns one instance, which the cardigan
    // fills, so there the dress is drawn as a companion "+" offset from it (Josh, 2026-10-22: a "+"
    // for the dress and one for the cardigan; offsets measured on the 17:52Z run with frames 208, 374
    // and 640 paused, and the last shot is too tight for the dress).
    //
    // Neither carries the other as "Also in this look" any more (Felipe, 2026-10-23: pressing Similar
    // on the cardigan showed the dress, not the similar items): each is reachable from the frame, and
    // the companion filled the one slot the model's similar items go in.
    product: SIMILAR_CARDIGAN,
    garments: [
      // The tracked point sits on the cardigan's front edge, against the print: nudged up and left onto
      // the grey knit so the "+" is clearly over the cardigan (Paula, 2026-10-24).
      { frames: [[23, 131]], at: [0.21, 0.71], offset: [-0.04, -0.08] },
      // No dress companion in the close-ups. A companion is drawn by shifting the CARDIGAN's path by a
      // fixed offset, and a fixed offset cannot follow a garment: it is right in the middle of each
      // shot and wrong at the ends, where the tracked point moves and the dress does not follow it.
      // Measured on the published path: at frame 384 the dress "+" sits on the print at (0.39, 0.93),
      // but by frame 409 the track has jumped to (0.41, 0.67) and the companion lands at (0.55, 0.82),
      // on the graffiti wall beside her (Felipe, 2026-10-23). The 179-237 shot ends the same way, at
      // y 0.99, on the very edge of the frame. The dress keeps its own "+" in the first Kelly shot,
      // where the detector gives it a real track that follows it; a hotspot on the wall is worse than
      // no hotspot. Giving it one here too needs a rerun with --merge_contain, not an offset.
      { frames: [[179, 237]], at: [0.28, 0.82], offset: [-0.02, 0] },
      { frames: [[339, 409]], at: [0.26, 0.77], offset: [-0.03, 0] },
      { frames: [[580, 697]], at: [0.12, 0.87] },
    ],
  },
  {
    // Kelly's dress, its own garment in the first Kelly shot since the 2026-10-23 run: the detector
    // returns it separately there, so it needs no companion offset and no nudge, unlike the close-ups
    // where the cardigan's track carries it. Its hotspot sits 0.11 of the frame below the cardigan's,
    // and each track takes the nearer of the two (Felipe, 2026-10-22: "in 2 it's fine to get the Noha
    // dress but in the subsequent we should get the cardigan", and 2026-10-23: "still have cardigan
    // and dress as same thing"). The 113-132 bodice track of the old run is hidden in curate now.
    product: REFORMATION_NOHA_DRESS,
    garments: [{ frames: [[23, 140]], at: [0.3, 0.78] }],
  },
];

const curate = curateWith(PLL_CONFIRMED);

/** The published PLL clip with the confirmed products on their garments. */
export function curatePllClip(clip: PublishedClip): PublishedClip {
  // No slug check: every clip of this show is a cut of the same minute starting at frame 0, so the
  // frame overlap in confirmed.ts decides, as the comment above PLL_CONFIRMED promises (jaysilvr, #104).
  return curate(clip);
}
